import { useEffect, useState } from 'react';
import { fetchTickets } from '../../lib/tickets';
import type { Ticket } from '../../types/tickets';
import { Modal } from '../ui/Modal';

type Props = {
  open: boolean;
  target: Ticket | null;
  busy?: boolean;
  onClose: () => void;
  onMerge: (sourceId: string) => Promise<void>;
};

export function TicketMergeModal({ open, target, busy, onClose, onMerge }: Props) {
  const [search, setSearch] = useState('');
  const [rows, setRows] = useState<Ticket[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedId, setSelectedId] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      setSearch('');
      setRows([]);
      setSelectedId('');
      setError(null);
      return;
    }
    const q = search.trim();
    const timer = window.setTimeout(() => {
      setLoading(true);
      setError(null);
      void fetchTickets({ search: q || undefined, page: 1, pageSize: 20 })
        .then((res) => {
          setRows(res.rows.filter((t) => t.id !== target?.id));
        })
        .catch((e) => {
          setError(e instanceof Error ? e.message : 'Search failed.');
          setRows([]);
        })
        .finally(() => setLoading(false));
    }, 220);
    return () => window.clearTimeout(timer);
  }, [open, search, target?.id]);

  async function handleConfirm() {
    if (!selectedId) {
      setError('Select a ticket to merge into this one.');
      return;
    }
    const source = rows.find((r) => r.id === selectedId);
    const label = source ? `${source.ticket_number} — ${source.subject}` : selectedId;
    if (
      !window.confirm(
        `Merge ${label} into ${target?.ticket_number ?? 'this ticket'}?\n\nMessages move here; the other ticket goes to Trash.`,
      )
    ) {
      return;
    }
    setError(null);
    try {
      await onMerge(selectedId);
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Merge failed.');
    }
  }

  return (
    <Modal open={open} onClose={onClose} title="Merge ticket into this conversation">
      <div className="ticket-merge-modal">
        <p className="ticket-merge-modal__lead">
          Choose another ticket whose messages should join{' '}
          <strong>{target?.ticket_number ?? 'this ticket'}</strong>. The selected ticket will be moved
          to Trash after merge.
        </p>
        <label className="ticket-compose-modal__field">
          <span>Search</span>
          <input
            className="field-input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Ticket number, subject, or requester…"
            disabled={busy}
          />
        </label>
        <div className="ticket-merge-modal__list" role="listbox" aria-label="Tickets to merge">
          {loading ? <p className="ticket-merge-modal__empty">Searching…</p> : null}
          {!loading && rows.length === 0 ? (
            <p className="ticket-merge-modal__empty">No matching tickets.</p>
          ) : null}
          {rows.map((row) => (
            <button
              key={row.id}
              type="button"
              role="option"
              aria-selected={selectedId === row.id}
              className={`ticket-merge-modal__row${selectedId === row.id ? ' is-selected' : ''}`}
              disabled={busy}
              onClick={() => setSelectedId(row.id)}
            >
              <span className="ticket-merge-modal__number">{row.ticket_number}</span>
              <span className="ticket-merge-modal__subject">{row.subject || '(no subject)'}</span>
              <span className="ticket-merge-modal__meta">
                {row.requester_name || row.requester_email || '—'}
              </span>
            </button>
          ))}
        </div>
        {error ? <p className="form-error">{error}</p> : null}
        <div className="ticket-compose-modal__actions-end" style={{ marginTop: 12 }}>
          <button type="button" className="btn-secondary btn-sm" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button
            type="button"
            className="btn-primary btn-sm"
            disabled={busy || !selectedId}
            onClick={() => void handleConfirm()}
          >
            {busy ? 'Merging…' : 'Merge'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
